import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Clock, CheckCircle2, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public consultation booking page at /book.
 *
 * Two-step UX:
 *   1. User picks a slot from the live availability grid (grouped by day).
 *   2. User fills in name/email/phone/topic and submits.
 *
 * After submission we show a confirmation card with the booking reference.
 * Slots already taken by another visitor are filtered server-side, but we
 * also re-fetch availability after a failed submit so the user sees the
 * latest open times.
 */
const Book = () => {
  const [availability, setAvailability] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', topic: '' });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const fetchSlots = async () => {
    setLoadingSlots(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/public/bookings/availability?days=14`);
      setAvailability(r.data);
    } catch {
      toast.error('Failed to load availability');
    } finally {
      setLoadingSlots(false);
    }
  };

  useEffect(() => { fetchSlots(); }, []);

  const slotsByDay = useMemo(() => {
    if (!availability?.slots) return {};
    const out = {};
    for (const s of availability.slots) {
      const day = s.start_at.slice(0, 10);
      out[day] = out[day] || [];
      out[day].push(s);
    }
    return out;
  }, [availability]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!picked) return toast.error('Pick a time slot first');
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.email.includes('@')) return toast.error('Valid email required');
    setSubmitting(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/public/bookings`, {
        ...form,
        start_at: picked.start_at,
      });
      setConfirmed(r.data);
      toast.success('Booking confirmed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to book');
      // Refresh slots in case the failure was a 409 conflict.
      fetchSlots();
      setPicked(null);
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmed) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-lg p-8 border-primary/40 bg-primary/5" data-testid="booking-confirmed">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-primary" />
          <h1 className="text-2xl font-black text-center mb-3">Booking confirmed</h1>
          <p className="text-sm text-center text-muted-foreground mb-4">
            We've sent a confirmation to <span className="text-foreground font-mono">{confirmed.email}</span>.
          </p>
          <div className="text-center space-y-2">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Your slot</p>
            <p className="text-base font-bold">
              {format(new Date(confirmed.start_at), 'EEEE, MMM dd, yyyy')}
            </p>
            <p className="text-sm">
              {format(new Date(confirmed.start_at), 'h:mm a')} ({confirmed.duration_minutes} min)
            </p>
            <p className="text-[10px] font-mono text-muted-foreground pt-3">Ref: {confirmed.booking_id}</p>
          </div>
          <Button className="w-full mt-6" variant="outline" onClick={() => window.location.href = '/'}>
            Back to home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="public-booking-page">
      <header className="border-b border-border bg-card/50 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <div className="text-2xl font-black">
              NEXT<span className="text-primary">/</span>GEN
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-1">
              Book a Consultation
            </div>
          </div>
          <a href="/" className="text-xs text-primary hover:underline">Back to site</a>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-6">
        <div>
          <h1 className="text-3xl sm:text-4xl font-black mb-2">Schedule a free consultation</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Pick a time that works — we'll spend {availability?.slot_duration_minutes || 30} minutes
            understanding your environment and recommending next steps. No commitment.
          </p>
        </div>

        <Card className="p-6 border-border" data-testid="booking-slot-picker">
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-4 h-4 text-primary" />
            <h2 className="font-bold uppercase tracking-tight text-sm">Available times</h2>
          </div>
          {loadingSlots && <Loader2 className="w-6 h-6 animate-spin mx-auto" />}
          {!loadingSlots && (!availability?.slots || availability.slots.length === 0) && (
            <p className="text-sm text-muted-foreground py-4">
              No slots available right now. Please call us at{' '}
              <a href="tel:7403025607" className="text-primary">740-302-5607</a>.
            </p>
          )}
          {!loadingSlots && Object.keys(slotsByDay).map((day) => (
            <div key={day} className="mb-4">
              <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {format(new Date(day + 'T00:00:00'), 'EEEE · MMM dd')}
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-6 gap-2">
                {slotsByDay[day].map((s) => {
                  const active = picked?.start_at === s.start_at;
                  return (
                    <button
                      key={s.start_at}
                      type="button"
                      onClick={() => setPicked(s)}
                      className={`px-3 py-2 text-xs uppercase tracking-wider rounded-md border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary font-bold' : 'border-border hover:border-primary/50'}`}
                      data-testid={`slot-${s.start_at}`}
                    >
                      {format(new Date(s.start_at), 'h:mm a')}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </Card>

        {picked && (
          <Card className="p-6 border-primary/30" data-testid="booking-form">
            <div className="flex items-center gap-2 mb-4">
              <Clock className="w-4 h-4 text-primary" />
              <h2 className="font-bold uppercase tracking-tight text-sm">
                Confirm — {format(new Date(picked.start_at), 'EEE, MMM dd · h:mm a')}
              </h2>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-input" data-testid="booking-name" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="bg-input" data-testid="booking-email" />
                </div>
              </div>
              <div>
                <Label>Phone (optional)</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-input" data-testid="booking-phone" />
              </div>
              <div>
                <Label>What would you like to discuss? (optional)</Label>
                <Textarea
                  rows={4} maxLength={2000}
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                  placeholder="e.g. We need to migrate 25 users to a new domain controller and want quotes."
                  className="bg-input"
                  data-testid="booking-topic"
                />
              </div>
              <Button type="submit" disabled={submitting} className="w-full font-bold uppercase tracking-widest" data-testid="booking-submit">
                {submitting ? 'Booking…' : 'Confirm Booking'}
              </Button>
            </form>
          </Card>
        )}
      </main>
    </div>
  );
};

export default Book;
